import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Grow, IconButton, Tooltip } from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { motion, useAnimationControls, useReducedMotion } from 'framer-motion';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useDecisionQueue } from '../../../contexts/DecisionQueueContext';
import { isUpdateNotification } from './notificationPresentation';
import { NotificationPopover } from './NotificationPopover';

const settled = (status: string) => status === 'ready' || status === 'error';

/**
 * The app-bar bell. The number counts only what is waiting on this person;
 * unread updates show as a dot. The bell nudges once when something new starts
 * waiting (never under reduced motion) instead of pulsing until everything is
 * read. What was already waiting when the app loaded, or comes back after an
 * Undo, does not count as new.
 */
export const NotificationBell: React.FC = () => {
  const { notifications, status } = useNotifications();
  const { items, waitingCount, leaveStatus, canDecideLeave } = useDecisionQueue();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const controls = useAnimationControls();
  const reduceMotion = useReducedMotion();
  const seenWaiting = useRef<Set<string> | null>(null);

  const unreadUpdates = useMemo(
    () => notifications.filter((notification) => isUpdateNotification(notification) && !notification.read).length,
    [notifications]
  );
  const waitingKey = items
    .filter((item) => !(item.kind === 'leave' && item.decision))
    .map((item) => item.id)
    .join('|');
  // 'idle' is not loaded: the providers subscribe in effects that run after
  // this one, so a baseline taken then would be empty and everything that was
  // already waiting would later count as new.
  const loaded = settled(status) && (settled(leaveStatus) || (!canDecideLeave && leaveStatus === 'idle'));

  useEffect(() => {
    if (!loaded) return;
    const waitingIds = waitingKey ? waitingKey.split('|') : [];
    if (!seenWaiting.current) {
      seenWaiting.current = new Set(waitingIds);
      return;
    }
    const seen = seenWaiting.current;
    const arrived = waitingIds.some((id) => !seen.has(id));
    waitingIds.forEach((id) => seen.add(id));
    if (arrived && !reduceMotion) {
      void controls.start({
        scale: [1, 1.18, 1],
        rotate: [0, -12, 10, 0],
        transition: { duration: 0.6, ease: 'easeOut' },
      });
    }
  }, [waitingKey, loaded, reduceMotion, controls]);

  const label =
    waitingCount > 0
      ? `Notifications, ${waitingCount} waiting on you`
      : unreadUpdates > 0
        ? 'Notifications, new updates'
        : 'Notifications';
  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Notifications" TransitionComponent={Grow}>
        <IconButton
          onClick={(event) => setAnchorEl(open ? null : event.currentTarget)}
          aria-label={label}
          aria-haspopup="dialog"
          aria-expanded={open}
          sx={{
            color: open ? 'white' : 'rgba(255, 255, 255, 0.8)',
            backgroundColor: open ? 'rgba(255, 255, 255, 0.12)' : undefined,
            transition: 'all 0.2s',
            '&:hover': {
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <Badge
            color={waitingCount > 0 ? 'error' : 'info'}
            variant={waitingCount > 0 ? 'standard' : 'dot'}
            // A dot ignores its content, but MUI hides a badge whose content is 0.
            badgeContent={waitingCount > 0 ? waitingCount : 1}
            invisible={waitingCount === 0 && unreadUpdates === 0}
          >
            <motion.div animate={controls} style={{ display: 'flex' }}>
              <NotificationsIcon />
            </motion.div>
          </Badge>
        </IconButton>
      </Tooltip>
      <NotificationPopover anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} />
    </>
  );
};

export default NotificationBell;
